'use client';

import React from 'react';
import styled, { keyframes } from 'styled-components';

export interface SkeletonProps {
  width?: string;
  height?: string;
  /** Border-radius — defaults to `radius.sm`. Pass `radius.pill` for circles/dots. */
  radius?: string;
  className?: string;
  /** Accessible label for the loading region. Defaults to "Loading". */
  'aria-label'?: string;
}

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const Block = styled.span<{ $w?: string; $h?: string; $r?: string }>`
  display: block;
  width: ${(p) => p.$w ?? '100%'};
  height: ${(p) => p.$h ?? '12px'};
  border-radius: ${(p) => p.$r ?? p.theme.radius.sm};
  background: linear-gradient(
    90deg,
    ${(p) => p.theme.colors.neutral.bg} 0%,
    ${(p) => p.theme.colors.neutral.line} 50%,
    ${(p) => p.theme.colors.neutral.bg} 100%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1.4s ease-in-out infinite;
`;

/**
 * Rectangular shimmer placeholder. Compose multiple skeletons in the
 * shape of the eventual content (a row of varied widths, a card with
 * lines and a thumbnail) so the layout doesn't shift when the content
 * resolves.
 */
export function Skeleton({
  width,
  height,
  radius,
  className,
  'aria-label': ariaLabel = 'Loading',
}: SkeletonProps) {
  return (
    <Block
      role="status"
      aria-busy="true"
      aria-label={ariaLabel}
      $w={width}
      $h={height}
      $r={radius}
      className={className}
    />
  );
}
